import { CustomerEnvironment } from "./saastypes";

/**
 * Multi-region eligibility, kept deliberately in step with the server.
 *
 * The peer dropdown is a convenience, not a control — the server re-derives every one of these
 * conditions from stored state on each call, and a cluster that slipped through here is refused there.
 * The reason to mirror the rules exactly is that a UI which disagrees with the server is worse than one
 * with no filter at all: it either offers clusters the server then rejects, or silently hides ones it
 * would have accepted.
 */

/** Minimum conductor version with active-passive replication support. */
export const MIN_CONDUCTOR_MAJOR = 5;
export const MIN_CONDUCTOR_MINOR = 5;

/**
 * The version part of a conductor image reference, e.g. `orkes-conductor:5.5.0-rc11` -> `5.5.0-rc11`.
 *
 * `ClusterDto.AppVersionsDto` strips the registry path but keeps `name:tag`, so the colon still has to
 * be handled here — same as the server's `MultiRegionSupport.conductorSemver`.
 */
export function conductorSemver(conductor?: string | null): string | null {
  if (!conductor) return null;
  const colon = conductor.lastIndexOf(":");
  return colon >= 0 ? conductor.slice(colon + 1) : conductor;
}

/**
 * Compares **major.minor only**, matching `MultiRegionSupport.supportsMultiRegion`.
 *
 * That is deliberate on both sides: a strict semver compare puts `5.5.0-rc11` *below* `5.5.0`, so the
 * release candidates actually running on beta today would be judged unsupported.
 */
export function supportsMultiRegion(conductor?: string | null): boolean {
  const version = conductorSemver(conductor);
  if (!version) return false;
  const parts = version.split(".");
  const major = intPrefix(parts[0]);
  const minor = parts.length > 1 ? intPrefix(parts[1]) : 0;
  if (major === null || minor === null) return false;
  return (
    major > MIN_CONDUCTOR_MAJOR ||
    (major === MIN_CONDUCTOR_MAJOR && minor >= MIN_CONDUCTOR_MINOR)
  );
}

/**
 * Clusters that could be peered with `currentClusterName`: a different cluster, not already in a pair,
 * and on a version that supports replication.
 *
 * `replicationRole` comes from the persisted columns, so a cluster peered out of band still reads as
 * standalone and will appear here. That is why the field stays free-form and the server does the
 * checking — see the `/status` probe in `peer_clusters` (CLOUD-1130).
 */
export function eligiblePeers(
  clusters: CustomerEnvironment[] | undefined,
  currentClusterName?: string
): CustomerEnvironment[] {
  if (!clusters) return [];
  return clusters.filter(
    (cluster) =>
      cluster.name !== currentClusterName &&
      !cluster.replicationRole &&
      supportsMultiRegion(cluster.appVersions?.conductor)
  );
}

/** Leading digits of a version part, so `0-rc11` reads as 0. Null when there are none. */
function intPrefix(part?: string): number | null {
  const digits = part?.match(/^\d+/);
  return digits ? Number(digits[0]) : null;
}
